import * as React from "react";

import { EnvBadge, Logo, LogoMark, useStableId } from "Components/ui";
import { cn } from "utils/cn";

// Abstract picture of what SAP does: one hub connected to many MetaHOS deployments. It shows the
// real rhythm of the TeleOpd link (each deployment pulls a signed snapshot from SAP and applies it)
// but no tenant data, since nobody has signed in yet. Under reduced motion it holds still.

const CX = 240;
const CY = 196;
const INNER_RADIUS = 92;
const RIPPLE_RADIUS = 180;
// One ripple at a time grows from 20% to 100% of RIPPLE_RADIUS over RIPPLE_SECONDS.
const RIPPLE_SECONDS = 10;

type NodeStatus = "live" | "due" | "onboarding";

interface GraphNode {
  x: number;
  y: number;
  radius: number;
  status: NodeStatus;
  parent?: number;
}

const polar = (radius: number, degrees: number) => {
  const radians = (degrees * Math.PI) / 180;
  return { x: CX + radius * Math.cos(radians), y: CY + radius * Math.sin(radians) };
};

const INNER: GraphNode[] = [-90, -30, 30, 90, 150, 210].map((angle) => ({
  ...polar(INNER_RADIUS, angle),
  radius: INNER_RADIUS,
  status: "live" as NodeStatus,
}));

const OUTER_STATUS: NodeStatus[] = [
  "live",
  "live",
  "due",
  "live",
  "live",
  "onboarding",
  "live",
  "live",
  "live",
  "due",
  "live",
  "live",
];

const OUTER: GraphNode[] = OUTER_STATUS.map((status, index) => {
  const angle = -75 + index * 30;
  const radius = index % 2 ? 158 : 172;
  // Each outer node hangs off the inner node whose angle is closest.
  const parent = Math.round(((angle + 90 + 360) % 360) / 60) % 6;
  return { ...polar(radius, angle), radius, status, parent };
});

const ONBOARDING = OUTER.findIndex((node) => node.status === "onboarding");

/**
 * One sync per inner node: a connected deployment under it asks SAP for its snapshot and gets the
 * signed answer back. Payment-due deployments are picked first, so their answers show in amber.
 */
const FLOWS = INNER.map((_, innerIndex) => {
  const children = OUTER.map((node, index) => ({ node, index })).filter(
    ({ node }) => node.parent === innerIndex && node.status !== "onboarding"
  );
  const pick =
    children.find(({ node }) => node.status === "due") || children[innerIndex % children.length];
  return { inner: innerIndex, outer: pick.index };
});

/** When the expanding ripple reaches a node (seconds into each period). */
const rippleDelay = (radius: number) => {
  const progress = (radius / RIPPLE_RADIUS - 0.2) / 0.8;
  return progress * RIPPLE_SECONDS;
};

const nodeFill: Record<NodeStatus, string> = {
  live: "fill-brand-teal",
  due: "fill-brand-warning",
  onboarding: "fill-brand-panel stroke-brand-line/50",
};

const path = (...points: Array<{ x: number; y: number }>) =>
  points
    .map((point, index) => `${index ? "L" : "M"}${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
    .join(" ");

/** Rotates an SVG group around the hub (CSS transforms default to the view box's origin). */
const aroundHub: React.CSSProperties = { transformOrigin: `${CX}px ${CY}px` };

const usePrefersReducedMotion = (): boolean => {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = React.useState<boolean>(
    () => typeof window !== "undefined" && Boolean(window.matchMedia?.(query).matches)
  );
  React.useEffect(() => {
    const media = window.matchMedia?.(query);
    if (!media) {
      return undefined;
    }
    const onChange = () => setReduced(media.matches);
    media.addEventListener?.("change", onChange);
    return () => media.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
};

interface PacketProps {
  id: string;
  route: string;
  begin: string;
  className: string;
  size?: number;
}

const PACKET_SECONDS = 3.2;

/** A dot that travels `route` once each time `begin` fires. */
const Packet: React.FC<PacketProps> = ({ id, route, begin, className, size = 2.2 }) => (
  // Hidden (and parked at the origin) until its motion runs.
  <circle r={size} className={className} opacity={0}>
    <animateMotion
      id={id}
      path={route}
      begin={begin}
      dur={`${PACKET_SECONDS}s`}
      calcMode="spline"
      keyTimes="0;1"
      keySplines="0.42 0 0.58 1"
    />
    {/* Visible only while moving (a constant <animate>; React has no <set>). */}
    <animate attributeName="opacity" values="0.85;0.85" begin={begin} dur={`${PACKET_SECONDS}s`} />
  </circle>
);

/**
 * The sync traffic, in SMIL so a packet can start when another arrives: a deployment's request
 * travels to SAP, the signed snapshot travels back, and the deployment flashes as it lands.
 */
const SyncTraffic: React.FC<{ id: string }> = ({ id }) => {
  const hub = { x: CX, y: CY };
  const arrivals: string[] = [];
  const flows = FLOWS.map((flow, index) => {
    const inner = INNER[flow.inner];
    const outer = OUTER[flow.outer];
    const requestId = `${id}-req-${index}`;
    const replyId = `${id}-rep-${index}`;
    const pause = 8 + (index % 3) * 2;
    arrivals.push(`${requestId}.end`);
    const due = outer.status === "due";
    return (
      <g key={index}>
        <Packet
          id={requestId}
          route={path(outer, inner, hub)}
          begin={`${(index * 2.6).toFixed(1)}s; ${replyId}.end+${pause}s`}
          className="fill-primary"
          size={1.8}
        />
        <Packet
          id={replyId}
          route={path(hub, inner, outer)}
          begin={`${requestId}.end+0.6s`}
          className={due ? "fill-brand-warning" : "fill-brand-teal"}
        />
        {/* The deployment lights up as its snapshot arrives. */}
        <circle
          cx={outer.x}
          cy={outer.y}
          r={4}
          opacity={0}
          className={cn("fill-none", due ? "stroke-brand-warning" : "stroke-brand-teal")}
          strokeWidth={1.5}
        >
          <animate
            attributeName="r"
            values="4;9"
            dur="1.6s"
            begin={`${replyId}.end`}
            fill="freeze"
          />
          <animate
            attributeName="opacity"
            values="0.5;0"
            dur="1.6s"
            begin={`${replyId}.end`}
            fill="freeze"
          />
        </circle>
      </g>
    );
  });

  return (
    <g>
      {flows}
      {/* SAP's ring answers every request that reaches it. */}
      <circle
        cx={CX}
        cy={CY}
        r={34}
        opacity={0}
        className="fill-none stroke-primary"
        strokeWidth={1.5}
      >
        <animate
          attributeName="r"
          values="34;42"
          dur="1.6s"
          begin={arrivals.join("; ")}
          fill="freeze"
        />
        <animate
          attributeName="opacity"
          values="0.35;0"
          dur="1.6s"
          begin={arrivals.join("; ")}
          fill="freeze"
        />
      </circle>
    </g>
  );
};

const NetworkGraphic: React.FC<{ className?: string; animated: boolean }> = ({
  className,
  animated,
}) => {
  const id = useStableId("network");
  const all = [...INNER, ...OUTER];

  return (
    <svg viewBox="0 0 480 392" aria-hidden="true" className={cn("overflow-visible", className)}>
      <defs>
        <radialGradient id={`${id}-hub`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#3A70D2" stopOpacity="0.24" />
          <stop offset="100%" stopColor="#3A70D2" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Broadcast ripple */}
      <circle
        cx={CX}
        cy={CY}
        r={RIPPLE_RADIUS}
        vectorEffect="non-scaling-stroke"
        style={{ ...aroundHub, animationDuration: `${RIPPLE_SECONDS}s` }}
        className="fill-none stroke-brand-teal/20 opacity-0 motion-safe:animate-ripple"
      />

      {/* Orbits, turning slowly in opposite directions */}
      <g style={aroundHub} className="motion-safe:animate-[spin_160s_linear_infinite]">
        <circle
          cx={CX}
          cy={CY}
          r={INNER_RADIUS}
          className="fill-none stroke-brand-line/[0.14]"
          strokeDasharray="2 6"
        />
      </g>
      <g style={aroundHub} className="motion-safe:animate-[spin_120s_linear_infinite_reverse]">
        <circle
          cx={CX}
          cy={CY}
          r={128}
          className="fill-none stroke-brand-line/[0.08]"
          strokeDasharray="1 9"
        />
      </g>
      <g style={aroundHub} className="motion-safe:animate-[spin_240s_linear_infinite]">
        <circle
          cx={CX}
          cy={CY}
          r={165}
          className="fill-none stroke-brand-line/[0.09]"
          strokeDasharray="2 8"
        />
      </g>

      {/* Links */}
      {INNER.map((node, index) => (
        <line
          key={`hub-${index}`}
          x1={CX}
          y1={CY}
          x2={node.x}
          y2={node.y}
          className="stroke-brand-line/[0.18]"
        />
      ))}
      {OUTER.map((node, index) => {
        const parent = INNER[node.parent ?? 0];
        const onboarding = index === ONBOARDING;
        return (
          <line
            key={`edge-${index}`}
            x1={parent.x}
            y1={parent.y}
            x2={node.x}
            y2={node.y}
            strokeDasharray={onboarding ? "3 7" : undefined}
            className={cn(
              onboarding
                ? "stroke-brand-line/35 motion-safe:animate-dash-flow [animation-duration:7s]"
                : "stroke-brand-line/[0.12]"
            )}
          />
        );
      })}

      {/* Mesh between the inner deployments, with data flowing round it */}
      {INNER.map((node, index) => {
        const next = INNER[(index + 1) % INNER.length];
        return (
          <g key={`mesh-${index}`}>
            <line
              x1={node.x}
              y1={node.y}
              x2={next.x}
              y2={next.y}
              className="stroke-brand-line/[0.07]"
            />
            <line
              x1={node.x}
              y1={node.y}
              x2={next.x}
              y2={next.y}
              strokeDasharray="1.5 11"
              strokeLinecap="round"
              style={{ animationDelay: `${index * -1.5}s`, animationDuration: "9s" }}
              className="stroke-brand-teal/25 motion-safe:animate-dash-flow"
            />
          </g>
        );
      })}

      {animated && <SyncTraffic id={id} />}

      {/* Deployments */}
      {all.map((node, index) => {
        const inner = index < INNER.length;
        const outerIndex = index - INNER.length;
        return (
          <g key={`node-${index}`}>
            {/* Glints as each broadcast ripple passes */}
            <circle
              cx={node.x}
              cy={node.y}
              r={inner ? 7 : 6}
              style={{
                animationDelay: `${rippleDelay(node.radius).toFixed(2)}s`,
                animationDuration: `${RIPPLE_SECONDS}s`,
              }}
              className={cn(
                "opacity-0 motion-safe:animate-node-flash",
                node.status === "due" ? "fill-brand-warning/20" : "fill-brand-teal/15"
              )}
            />
            {node.status === "due" && (
              <circle
                cx={node.x}
                cy={node.y}
                r={7.5}
                style={{ animationDuration: "5s" }}
                className="fill-brand-warning/15 motion-safe:animate-breathe"
              />
            )}
            {outerIndex === ONBOARDING && (
              <circle
                cx={node.x}
                cy={node.y}
                r={8}
                strokeDasharray="2 3"
                className="fill-none stroke-brand-line/50 [transform-box:fill-box] [transform-origin:center] motion-safe:animate-[spin_14s_linear_infinite]"
              />
            )}
            <circle
              cx={node.x}
              cy={node.y}
              r={inner ? 4.5 : 3.5}
              className={cn("stroke-brand-panel", nodeFill[node.status])}
              strokeWidth={node.status === "onboarding" ? 1.25 : 2}
            />
          </g>
        );
      })}

      {/* SAP hub: halo, tick ring, two counter-turning arcs and the mark */}
      <circle
        cx={CX}
        cy={CY}
        r={72}
        fill={`url(#${id}-hub)`}
        style={{ animationDuration: "9s" }}
        className="motion-safe:animate-breathe"
      />
      <g style={aroundHub} className="motion-safe:animate-[spin_180s_linear_infinite_reverse]">
        <circle
          cx={CX}
          cy={CY}
          r={52}
          className="fill-none stroke-brand-line/25"
          strokeDasharray="1 7.17"
        />
      </g>
      <g style={aroundHub} className="motion-safe:animate-[spin_28s_linear_infinite]">
        <circle
          cx={CX}
          cy={CY}
          r={43}
          className="fill-none stroke-primary/45"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeDasharray="36 234"
        />
      </g>
      <g style={aroundHub} className="motion-safe:animate-[spin_40s_linear_infinite_reverse]">
        <circle
          cx={CX}
          cy={CY}
          r={43}
          className="fill-none stroke-brand-teal/35"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeDasharray="18 252"
        />
      </g>
      <circle cx={CX} cy={CY} r={34} className="fill-brand-panel stroke-brand-line/20" />
      {/* A nested <svg> takes its size from attributes; CSS width/height are ignored there. */}
      <LogoMark x={CX - 17} y={CY - 17} width={34} height={34} />
    </svg>
  );
};

const LEGEND: Array<{ label: string; status: NodeStatus }> = [
  { label: "Live", status: "live" },
  { label: "Payment due", status: "due" },
  { label: "Onboarding", status: "onboarding" },
];

const legendDot: Record<NodeStatus, string> = {
  live: "bg-brand-teal",
  due: "bg-brand-warning",
  onboarding: "border border-brand-line/60",
};

// What the control plane does all day, in general terms (no tenant data before sign-in).
const ACTIVITY = [
  "snapshot signed: billing state + modules",
  "deployment pulled its snapshot",
  "payment warning scheduled",
  "module access applied",
  "audit entry sealed",
];
const ACTIVITY_SECONDS = 4.5;

/** One line of control-plane activity at a time, rolling upwards. */
const ActivityFeed: React.FC<{ animated: boolean }> = ({ animated }) => {
  const [index, setIndex] = React.useState(0);
  const [instant, setInstant] = React.useState(false);

  React.useEffect(() => {
    if (!animated) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setInstant(false);
      setIndex((value) => value + 1);
    }, ACTIVITY_SECONDS * 1000);
    return () => window.clearInterval(timer);
  }, [animated]);

  // The list ends with a copy of the first line; on reaching it, jump back without a transition.
  const onRolled = () => {
    if (index >= ACTIVITY.length) {
      setInstant(true);
      setIndex(0);
    }
  };

  return (
    <div className="mt-5 h-4 overflow-hidden font-mono text-[11px] text-brand-panel-foreground/50">
      <ul
        onTransitionEnd={onRolled}
        style={{ transform: `translateY(-${index}rem)` }}
        className={cn(!instant && "transition-transform duration-700 ease-standard")}
      >
        {[...ACTIVITY, ACTIVITY[0]].map((line, position) => (
          <li key={position} className="flex h-4 items-center justify-center gap-2 leading-4">
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-brand-teal/70" />
            <span className="text-brand-panel-foreground/35">sap</span>
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
};

const BrandPanel: React.FC<{ className?: string }> = ({ className }) => {
  const reduced = usePrefersReducedMotion();
  const panel = React.useRef<HTMLElement | null>(null);
  const frame = React.useRef<number | null>(null);

  // Gentle parallax: layers drift a few pixels towards the pointer, at different depths.
  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const element = panel.current;
    if (reduced || !element || frame.current !== null) {
      return;
    }
    const { clientX, clientY } = event;
    frame.current = window.requestAnimationFrame(() => {
      frame.current = null;
      const box = element.getBoundingClientRect();
      const x = ((clientX - box.left) / box.width) * 2 - 1;
      const y = ((clientY - box.top) / box.height) * 2 - 1;
      element.style.setProperty("--px", x.toFixed(3));
      element.style.setProperty("--py", y.toFixed(3));
    });
  };
  const onPointerLeave = () => {
    panel.current?.style.setProperty("--px", "0");
    panel.current?.style.setProperty("--py", "0");
  };
  React.useEffect(
    () => () => {
      if (frame.current !== null) window.cancelAnimationFrame(frame.current);
    },
    []
  );

  const drift = (depth: number): React.CSSProperties => ({
    transform: `translate3d(calc(var(--px, 0) * ${depth}px), calc(var(--py, 0) * ${depth}px), 0)`,
  });

  return (
    <aside
      ref={panel}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      className={cn(
        "relative flex-col justify-between overflow-hidden border-r border-border bg-brand-panel px-10 py-9 text-brand-panel-foreground xl:px-14 xl:py-12",
        className
      )}
    >
      {/* Dot grid, fading out towards the edges */}
      <div
        aria-hidden="true"
        style={drift(-3)}
        className="pointer-events-none absolute -inset-4 bg-[radial-gradient(rgb(var(--brand-line)/0.1)_1px,transparent_1px)] transition-transform duration-700 ease-out [background-size:22px_22px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_45%,black,transparent)]"
      />
      <div
        aria-hidden="true"
        style={drift(8)}
        className="pointer-events-none absolute -bottom-40 -left-24 h-[28rem] w-[28rem] rounded-full bg-primary/[0.12] blur-[120px] transition-transform duration-700 ease-out"
      />

      <div className="relative flex items-center justify-between gap-4">
        <Logo />
        <EnvBadge />
      </div>

      <div className="relative mx-auto w-full max-w-[30rem] py-8">
        <div style={drift(5)} className="transition-transform duration-1000 ease-out">
          <NetworkGraphic className="w-full" animated={!reduced} />
        </div>
        <ul className="mt-6 flex justify-center gap-5 font-mono text-[11px] uppercase tracking-wider text-brand-panel-foreground/60">
          {LEGEND.map((item) => (
            <li key={item.status} className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className={cn("h-2 w-2 rounded-full", legendDot[item.status])}
              />
              {item.label}
            </li>
          ))}
        </ul>
        <ActivityFeed animated={!reduced} />
      </div>

      <div className="relative space-y-3">
        <h2 className="max-w-md text-[1.75rem] font-semibold leading-tight tracking-tight">
          One control plane for every MetaHOS hospital.
        </h2>
        <p className="max-w-md text-sm leading-6 text-brand-panel-foreground/70">
          Billing, module access and audit trails for each deployment, managed from one place.
        </p>
        <p className="pt-6 text-xs text-brand-panel-foreground/50">
          © {new Date().getFullYear()} Meta16 Labs
        </p>
      </div>
    </aside>
  );
};

export default BrandPanel;
